<template>
  <div
    tabindex="0"
    :class="['focus-area', 'blurred']"
    :data-focus-area="areaId"
    @focusin="onFocusIn"
    @blur="onBlur"
    @keydown="onKeyDown"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';

import { useFocusManager } from '@/stores/focusManager';

const props = defineProps<{
  areaId: string;
}>();

const focusManager = useFocusManager();
const lastInteraction = ref<'keyboard' | 'mouse'>('keyboard');

const handleMouseDown = (_e: MouseEvent) => {
  lastInteraction.value = 'mouse';
};

function getAreaElement(e?: Event): HTMLElement | null {
  return (
    (e?.target as HTMLElement | null | undefined) ??
    document.querySelector<HTMLElement>(`[data-focus-area="${props.areaId}"]`)
  );
}

const highlight = (event?: FocusEvent | KeyboardEvent) => {
  const element = getAreaElement(event);
  if (!element) return;

  if (element.classList.contains('focus-area')) {
    element.classList.add('focused');
    element.classList.remove('blurred');
  }
  if (lastInteraction.value === 'mouse') return;

  setTimeout(() => {
    element.classList.remove('focus-visible');
  }, 600);

  if (element.classList.contains('focus-area')) {
    element.classList.add('focus-visible');
  }
};

const lowlight = (event?: FocusEvent | KeyboardEvent) => {
  const element = getAreaElement(event);
  if (!element) return;

  element.classList.remove('focus-visible', 'focused');
  if (element.classList.contains('focus-area')) {
    element.classList.add('blurred');
  }
};

const onFocusIn = (event: FocusEvent) => {
  focusManager.updateFocusIndexByID(props.areaId);
  highlight(event);
};

const onBlur = (event: FocusEvent) => {
  lowlight(event);
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Tab' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    lastInteraction.value = 'keyboard';
    highlight(event);
  }
};

const refreshHighlight = (newIndex: number, oldIndex: number) => {
  const currentAreaId = focusManager.focusAreas[newIndex] || null;
  if (currentAreaId === props.areaId) {
    highlight();
  } else if (focusManager.focusAreas[oldIndex] === props.areaId) {
    lowlight();
  }
};

watch(
  () => focusManager.currentFocusIndex,
  (newIndex, oldIndex) => {
    refreshHighlight(newIndex, oldIndex);
  },
);

onMounted(() => {
  focusManager.registerFocusArea(props.areaId);
  window.addEventListener('mousedown', handleMouseDown);
  refreshHighlight(focusManager.currentFocusIndex, -1);
});

onUnmounted(() => {
  focusManager.unregisterFocusArea(props.areaId);
  window.removeEventListener('mousedown', handleMouseDown); // <- remove, nicht add
});
</script>

<style scoped lang="css">
@layer app {
  .focus-area {
    box-shadow: none;
    transition: box-shadow 0.3s ease;
  }

  .focus-area.focus-visible {
    box-shadow: inset 0 0 1px 2px var(--area-focus); /* Innerer Rand mit Highlight */
  }
}
</style>
